/**
 * Parts Master extra-column registry.
 *
 * Some tenants (e.g. PNA / SAP B1) push extra fields per part — customer,
 * lead time, MOQ, FSC/PEFC compliance, commodity code, etc. Other tenants
 * push only the basics. The base table renders the universal columns
 * (Part #, Description, Commodity, UOM, Firm Days, Schedule, Status,
 * Updated). The extras here render *only when at least one row in the
 * current page has the corresponding field populated* — keeps the table
 * tidy on tenants that don't use them, surfaces them automatically when
 * the data arrives.
 */
import type { Part } from '../lib/api/types/parts';

export interface ExtraColumn {
  key: keyof Part;
  label: string;
  /** If true, render in a right-aligned tabular-nums cell. */
  numeric?: boolean;
  /** Optional formatter; defaults to `String(v)`. */
  format?: (v: unknown) => string;
  className?: string;
  /**
   * Backend ordering field (`?ordering=`). Defaults to `key` — only set when the
   * serializer alias differs from the model path (Tier-2 extension fields).
   */
  sortKey?: string;
}

const decimalOrDash = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
};

const yesNoOrDash = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '—';
  // SAP BoYesNoEnum string form ("tYES" / "tNO") or already 'Y' / 'N'
  const s = String(v).toUpperCase();
  if (s === 'TYES' || s === 'Y' || s === 'YES') return 'Yes';
  if (s === 'TNO' || s === 'N' || s === 'NO') return 'No';
  return s;
};

export const EXTRA_COLUMNS: ExtraColumn[] = [
  // Identity
  { key: 'alt_part_number', label: 'Alt Part #' },
  { key: 'second_description', label: 'Secondary Desc', className: 'max-w-[200px] whitespace-normal break-words' },
  { key: 'bar_code', label: 'Barcode' },

  // Customer
  { key: 'default_customer_code', label: 'Customer' },
  { key: 'default_customer_name', label: 'Customer Name', className: 'max-w-[160px] whitespace-normal break-words' },

  // Planning
  { key: 'planning_system', label: 'Planning' },
  { key: 'procurement_method', label: 'Procurement' },
  { key: 'lead_time_days', label: 'Lead (d)', numeric: true, format: decimalOrDash },
  { key: 'tolerance_days', label: 'Tolerance (d)', numeric: true, format: decimalOrDash },
  { key: 'min_order_qty', label: 'MOQ', numeric: true, format: decimalOrDash },
  { key: 'order_multiple', label: 'Multiple', numeric: true, format: decimalOrDash },

  // Pallet / load
  { key: 'pallet_qty', label: 'Pallet Qty', numeric: true, format: decimalOrDash },
  { key: 'load_qty', label: 'Load Qty', numeric: true, format: decimalOrDash },

  // Stock summary (item-level, default warehouse from OITM)
  { key: 'default_warehouse', label: 'Default WH' },
  { key: 'on_hand_qty', label: 'On Hand', numeric: true, format: decimalOrDash },
  { key: 'min_stock_level', label: 'Min Stock', numeric: true, format: decimalOrDash },
  { key: 'max_stock_level', label: 'Max Stock', numeric: true, format: decimalOrDash },

  // Units of measure
  { key: 'sales_uom', label: 'Sales UOM' },
  { key: 'purchase_uom', label: 'Purchase UOM' },
  { key: 'sales_pack_uom', label: 'Sales Pack UOM' },
  { key: 'sales_pack_units', label: 'Sales Pack Units', numeric: true, format: decimalOrDash },
  { key: 'sales_items_per_unit', label: 'Items / Sales Unit', numeric: true, format: decimalOrDash },
  { key: 'purchase_items_per_unit', label: 'Items / Purchase Unit', numeric: true, format: decimalOrDash },

  // Weights
  { key: 'weight_sales', label: 'Weight Sales (kg)', numeric: true, format: decimalOrDash },
  { key: 'weight_purchase', label: 'Weight Purchase (kg)', numeric: true, format: decimalOrDash },
  { key: 'packaging_plastic_weight', label: 'Plastic Pack (kg)', numeric: true, format: decimalOrDash },
  { key: 'packaging_wood_weight', label: 'Wood Pack (kg)', numeric: true, format: decimalOrDash },
  { key: 'packaging_metal_weight', label: 'Metal Pack (kg)', numeric: true, format: decimalOrDash },

  // Sales dimensions
  { key: 'sales_height', label: 'Sales H', numeric: true, format: decimalOrDash },
  { key: 'sales_width', label: 'Sales W', numeric: true, format: decimalOrDash },
  { key: 'sales_length', label: 'Sales L', numeric: true, format: decimalOrDash },
  { key: 'sales_volume', label: 'Sales Vol', numeric: true, format: decimalOrDash },

  // Purchase dimensions
  { key: 'purchase_height', label: 'Purchase H', numeric: true, format: decimalOrDash },
  { key: 'purchase_width', label: 'Purchase W', numeric: true, format: decimalOrDash },
  { key: 'purchase_length', label: 'Purchase L', numeric: true, format: decimalOrDash },
  { key: 'purchase_volume', label: 'Purchase Vol', numeric: true, format: decimalOrDash },

  // Compliance (Tier-2 — PNA-only data, but the column is universal)
  { key: 'fsc_compliant', label: 'FSC', format: yesNoOrDash, sortKey: 'import_extension__fsc_compliant' },
  { key: 'fsc_prod_num', label: 'FSC Prod #', sortKey: 'import_extension__fsc_prod_num' },
  { key: 'pefc_compliant', label: 'PEFC', format: yesNoOrDash, sortKey: 'import_extension__pefc_compliant' },
  { key: 'pefc_prod_num', label: 'PEFC Prod #', sortKey: 'import_extension__pefc_prod_num' },
  { key: 'compliance_level', label: 'Compliance %', className: 'max-w-[90px]', sortKey: 'import_extension__compliance_level' },

  // Commodity / business codes
  { key: 'ext_commodity_code', label: 'Commodity Code', className: 'min-w-[140px] max-w-[160px] whitespace-normal break-words', sortKey: 'import_extension__commodity_code' },
  { key: 'pmx_bcty', label: 'BCTY', sortKey: 'import_extension__pmx_bcty' },
  { key: 'u_volume_text', label: 'U_Volume', sortKey: 'import_extension__u_volume_text' },

  // PNA price-list / channel flags (OITM.QryGroupN)
  { key: 'qg_zest', label: 'Zest', format: yesNoOrDash, sortKey: 'import_extension__qg_zest' },
  { key: 'qg_zest_direct', label: 'Zest Direct', format: yesNoOrDash, sortKey: 'import_extension__qg_zest_direct' },
  { key: 'qg_woodworks_gc', label: 'Woodworks GC', format: yesNoOrDash, sortKey: 'import_extension__qg_woodworks_gc' },
  { key: 'qg_zest_gc_pricelist', label: 'Zest GC PL', format: yesNoOrDash, sortKey: 'import_extension__qg_zest_gc_pricelist' },
  { key: 'qg_zest_pricelist_upload', label: 'Zest PL Upload', format: yesNoOrDash, sortKey: 'import_extension__qg_zest_pricelist_upload' },
  { key: 'qg_zest_direct_pricelist', label: 'Zest Direct PL', format: yesNoOrDash, sortKey: 'import_extension__qg_zest_direct_pricelist' },
];

/** True if any row in the page has at least one of the extra columns populated. */
export function hasExtraColumnData(rows: Part[]): boolean {
  return rows.some(row =>
    EXTRA_COLUMNS.some(col => {
      const v = row[col.key];
      return v !== null && v !== undefined && v !== '';
    }),
  );
}

/** Handler-driven ERP integrations whose tenants see the SAP-shape columns.
 * Adding a new tenant here is a one-line change.
 */
const HANDLERS_WITH_EXTRAS = new Set(['pna']);

/**
 * Should the SAP extras block be available at all?
 *
 * Strict tenant gating: a user only sees these columns when their own tenant's
 * import handler is on the allowlist. The previous implementation also fell
 * back to "any row in the visible page has the data", which meant:
 *
 *   - Users with `import_handler=null` (customers, Helix support staff,
 *     logistics, anyone whose own company isn't a supplier with a
 *     SupplierImportProfile) saw all PNA-shape columns as soon as a single
 *     row in the page had any of those fields populated.
 *   - On a multi-tenant install, a non-PNA supplier admin who happened to
 *     view a part with PNA-shape data populated would see PNA columns leak
 *     into their table.
 *
 * The handler is the source of truth. Per-column "is anything populated?"
 * trimming still happens inside `buildAllColumnDescriptors` so the dropdown
 * doesn't list 30 empty extras on a fresh PNA install.
 */
export function shouldShowExtraColumns(
  _rows: Part[],
  importHandler: string | null | undefined,
): boolean {
  return !!(importHandler && HANDLERS_WITH_EXTRAS.has(importHandler));
}

/** Handlers that use the Part Schedule module (Firm Days / Schedule columns). */
const HANDLERS_WITH_SCHEDULE = new Set(['dennis_eagle']);

export function shouldShowScheduleColumns(
  rows: Part[],
  importHandler: string | null | undefined,
): boolean {
  if (importHandler && HANDLERS_WITH_SCHEDULE.has(importHandler)) {
    return true;
  }
  return rows.some(p => p.schedule_firm !== null || p.schedule_active !== null);
}
